import { useCallback, useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Field } from '../../../components/field/field';
import { Typography } from '../../../components/typography/typography';
import { toast } from '../../../util/toasts';
import classNames from 'classnames';
import { debounce } from 'lodash';
import usePrevious from '../../../hooks/use-previous';
import { Radio } from '../../../components/radio/radio';

const SEQUENCE_TOKEN = new RegExp('{[Ss]([6-9]|1[012])}+', 'g');

export const OnAccountNumberFormatConfig = () => {
  const { getMultipleConfigurations, updateOnAccountNumberFormat } = useAPI();

  const [isOnAccountEnabled, setIsOnAccountEnabled] = useState<boolean>();
  const [rawPattern, setRawPattern] = useState<string>();
  const [formattedPattern, setFormattedPattern] = useState<string>();
  const [isValid, setIsValid] = useState(false);
  const previousPattern = usePrevious(rawPattern);

  const sanitizePattern = (pattern: string) => {
    return pattern
      .trim()
      .replace(/\s+/g, '-')
      .replace(SEQUENCE_TOKEN, (match) => match.toUpperCase());
  };

  const toFormatPattern = (pattern: string): [string, boolean] => {
    const sanitized = sanitizePattern(pattern);
    const isValid = SEQUENCE_TOKEN.test(sanitized);
    setIsValid(isValid);

    return [
      sanitized.replace(SEQUENCE_TOKEN, (match) => {
        const length = parseInt(match.slice(2, -1));
        return '1'.padStart(length, '0');
      }),
      isValid,
    ];
  };

  const doUpdate = useCallback(
    debounce((pattern: string) => {
      updateOnAccountNumberFormat(sanitizePattern(pattern)).catch((err) => {
        console.error(err);
        toast('Failed to update account number format', 'error');
      });
    }, 1000),
    [],
  );

  useEffect(() => {
    if (rawPattern === undefined) return;

    const [f, v] = toFormatPattern(rawPattern);
    setFormattedPattern(f);
    previousPattern && previousPattern !== rawPattern && v && doUpdate(rawPattern);
  }, [rawPattern]);

  useEffect(() => {
    getMultipleConfigurations(['CONF_RENT_ON_ACCOUNT', 'CONF_ACCOUNT_NUMBER_FORMAT'])
      .then((conf) => {
        setIsOnAccountEnabled(conf.find((c) => c.variable === 'CONF_RENT_ON_ACCOUNT')?.value === '1');
        setRawPattern(conf.find((c) => c.variable === 'CONF_ACCOUNT_NUMBER_FORMAT')?.value || '');
      })
      .catch(() => toast('Failed to load account number format', 'error'));
  }, []);

  return (
    <>
      <div className="flex flex-col gap-1">
        <div className="flex gap-2">
          <Radio
            name="CONF_RENT_ON_ACCOUNT"
            label="No"
            isChecked={!isOnAccountEnabled}
            value="0"
            onChange={(checked) => checked && setIsOnAccountEnabled(false)}
          />
          <Radio
            name="CONF_RENT_ON_ACCOUNT"
            label="Yes"
            isChecked={isOnAccountEnabled}
            value="1"
            onChange={(checked) => checked && setIsOnAccountEnabled(true)}
          />
        </div>

        <Typography type="text-xs" className="!italic !text-grey-700">
          Turn on/off RENT ON ACCOUNT feature. When enabled, the account number format will be used to generate account
          numbers.e.
        </Typography>
      </div>
      {rawPattern !== undefined && isOnAccountEnabled && (
        <div>
          <Field type="text" value={rawPattern} onChange={setRawPattern} invalid={!isValid} />

          <Typography
            type="text-xs"
            className={classNames('!italic', { '!text-grey-700': isValid, '!text-error-500': !isValid })}
          >
            Format of the account number.
            <br />
            Result: <b>{formattedPattern}</b>
          </Typography>

          <Typography type="text-xs" className="!text-grey-700 !italic">
            Available tokens:
            <br />- <b>{`{S[6-12]}:`}</b> Sequence number padded to the specified number of digits, minimum 6, maximum
            12. At least one sequence token is required in the format.
          </Typography>
        </div>
      )}
    </>
  );
};
